import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Params, Router } from '@angular/router';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { ElementActions, ElementTypes, GaContext, GtmTripType, LoaderType } from '@fcom/common/interfaces';
import { WindowRef } from '@fcom/core/providers/window';
import { ConfigService, NativeBridgeService } from '@fcom/core/services';
import { IdAndHash } from '@fcom/dapi';
import { isPresent, unique, unsubscribe } from '@fcom/core/utils';
import { finShare } from '@fcom/rx';
import { FinnairOrder, FinnairServiceItem, ServiceFlow } from '@fcom/dapi/api/models';
import { QualtricsSurvey, QualtricsSurveys } from '@fcom/common/qualtrics/qualtrics.utils';
import { OrderStatus } from '@fcom/common-booking/store';
import { ButtonMode, ButtonTheme, IconPosition, NotificationLayout, NotificationTheme } from '@fcom/ui-components';
import { RootPaths } from '@fcom/core/constants';
import { GtmService } from '@fcom/common/gtm';
import { getServicesToShow } from '@fcom/common-booking/modules/ancillaries/utils';
import { getTripTypeFromBounds } from '@fcom/common/utils/gtm.utils';

import { GrandTotalTheme } from '../../../common/components/grand-total/grand-total.component';

export enum PageState {
  LOADING,
  DONE,
  ERROR,
}

@Component({
  selector: 'fin-purchase-success',
  templateUrl: 'purchase-success.component.html',
  styleUrl: 'purchase-success.component.scss',
})
export class PurchaseSuccessComponent implements OnInit, OnDestroy {
  readonly PageState = PageState;
  readonly LoaderType = LoaderType;
  readonly ServiceFlow = ServiceFlow;
  readonly ElementTypes = ElementTypes;
  readonly TRACKING_CONTEXT = GaContext.BOOKING_FLOW;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly NotificationTheme = NotificationTheme;
  readonly GrandTotalTheme = GrandTotalTheme;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;

  @Input()
  orderStatus$: Observable<OrderStatus>;
  @Input()
  orderData$: Observable<FinnairOrder>;
  @Input()
  orderFetchInformation$: Observable<IdAndHash>;

  showCorporateUpsell$: Observable<boolean>;
  hasCorporateSeatBenefit$: Observable<boolean>;

  @Output()
  loadOrderErrorClick: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output()
  ctaClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  surveyInfo$: Observable<QualtricsSurvey>;

  state$: Observable<PageState>;
  appUrl$: Observable<string>;
  hasBeenIssuedETickets$: Observable<boolean>;
  unconfirmedAndPendingServices$: Observable<FinnairServiceItem[]>;
  paidIncludedServices$: Observable<FinnairServiceItem[]>;

  subscriptions: Subscription;

  showPoints$: Observable<boolean>;
  emails$: Observable<string[]>;
  openSurvey = false;
  enableAppCtaButton = false;
  enableSynchronousTicketingOnConfirmationPage = false;

  private removeGlobalListener: () => void;

  constructor(
    public nativeBridgeService: NativeBridgeService,
    private windowRef: WindowRef,
    public languageService: LanguageService,
    public router: Router,
    private gtmService: GtmService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.enableSynchronousTicketingOnConfirmationPage =
      this.configService.cfg.enableSynchronousTicketingOnConfirmationPage;
    this.surveyInfo$ = this.orderData$.pipe(map((od) => this.getSurveyInfo(od)));

    this.showPoints$ = this.orderData$.pipe(
      map(
        (order) =>
          order.serviceFlow === ServiceFlow.AWARD ||
          !!(!order.serviceFlow && order.prices?.included?.total?.total?.totalPoints)
      )
    );

    this.state$ = combineLatest({
      status: this.orderStatus$,
      order: this.orderData$.pipe(startWith(undefined as FinnairOrder)),
      fetchInfo: this.orderFetchInformation$,
    }).pipe(
      map(({ status, order, fetchInfo }: { status: OrderStatus; order: FinnairOrder; fetchInfo: IdAndHash }) => {
        if (status === OrderStatus.LOAD_ERROR) {
          return PageState.ERROR;
        }
        if (isPresent(order) && isPresent(order.id) && order.id === fetchInfo.id) {
          return PageState.DONE;
        }
        return PageState.LOADING;
      }),
      distinctUntilChanged()
    );

    this.hasBeenIssuedETickets$ = this.orderData$.pipe(
      map((orderData) => {
        return orderData.passengers.every((passenger) => passenger.etickets?.length);
      })
    );
    this.unconfirmedAndPendingServices$ = this.orderData$.pipe(
      map((booking) => [
        ...getServicesToShow(booking.services, 'unconfirmed'),
        ...getServicesToShow(booking.services, 'pending'),
      ]),
      finShare()
    );
    this.paidIncludedServices$ = this.orderData$.pipe(
      map((booking) => {
        return getServicesToShow(booking.services, 'included', true);
      })
    );

    this.appUrl$ = this.orderData$.pipe(
      filter((data) => isPresent(data) && this.nativeBridgeService.isInsideNativeWebView),
      map((data: FinnairOrder) => {
        return this.nativeBridgeService.urlForAddAJourneyInApp(data.id, data.passengers[0].lastName);
      })
    );

    this.subscriptions = this.orderData$.pipe(filter((data) => isPresent(data))).subscribe((data: FinnairOrder) => {
      if (this.nativeBridgeService.isInsideNativeWebView) {
        this.nativeBridgeService.addBooking({
          recloc: data.id,
          travelers: data.passengers,
          rawBooking: data,
        });
      }
    });

    this.emails$ = this.orderData$.pipe(
      map((orderData) => {
        return orderData.passengers
          .map((passenger) => {
            return passenger.email;
          })
          .filter((email) => Boolean(email))
          .map((email) => email.toLowerCase())
          .filter(unique);
      })
    );

    this.showCorporateUpsell$ = this.orderData$.pipe(
      map(
        (orderData: FinnairOrder) =>
          this.configService.cfg.corporatePredict &&
          orderData.serviceFlow === ServiceFlow.BOOKING &&
          orderData.otherInformation.predictions?.corporateProbability > 0.6 &&
          orderData.otherInformation.predictions?.corporateProbability <= 1.0
      )
    );

    this.hasCorporateSeatBenefit$ = this.orderData$.pipe(
      map((orderData: FinnairOrder) => orderData.otherInformation?.corporateUserInformation?.isEligibleForSeatBenefit)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);

    if (this.removeGlobalListener) {
      this.removeGlobalListener();
    }
  }

  openInApp(appUrl: string): void {
    this.windowRef.nativeWindow.open(appUrl);
  }

  openModal(event: Event): void {
    event.stopPropagation();

    this.openSurvey = true;
  }

  goToHomePage(): void {
    this.router.navigate([this.languageService.langValue]);
    this.gtmService.trackElement('home-button', this.TRACKING_CONTEXT, ElementTypes.BUTTON, '', ElementActions.CLICK);
  }

  goToMMB(extraParams?: Params): void {
    this.subscriptions.add(
      this.orderData$.pipe(take(1)).subscribe((order) => {
        const queryParams = {
          recLoc: order.id,
          lastName: order.passengers[0].lastName,
          ...extraParams,
        };
        const urlTree = this.router.createUrlTree(
          [`${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}`],
          { queryParams }
        );
        const url = this.router.serializeUrl(urlTree);
        this.router.navigateByUrl(url);

        this.gtmService.trackElement(
          'mmb-button',
          this.TRACKING_CONTEXT,
          ElementTypes.BUTTON,
          '',
          ElementActions.CLICK
        );
      })
    );
  }

  openCorporateSeatBenefit(): void {
    this.goToMMB({ seatMap: true });
  }

  private getSurveyInfo(orderData: FinnairOrder): QualtricsSurvey {
    if (getTripTypeFromBounds(orderData.bounds) == GtmTripType.MULTICITY) {
      return QualtricsSurveys.CONFIRMATION_MULTI_CITY;
    }

    return orderData.serviceFlow === ServiceFlow.AWARD
      ? QualtricsSurveys.AWARD_FEEDBACK
      : QualtricsSurveys.PURCHASE_FEEDBACK;
  }
}
